// src/pages/app/DashboardPage.jsx — Role-specific tailored workflows (Employee -> Manager -> Travel Desk -> Finance -> Admin)
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { reportsApi, travelRequestsApi, expenseClaimsApi } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plane, Receipt, Wallet, Clock, CheckCircle2, XCircle,
  TrendingUp, AlertTriangle, BarChart3, Plus, ArrowUpRight,
  ShieldCheck, PieChart as PieChartIcon, MapPin, Calendar,
  Building2, Users, FileCheck, Check, Sparkles, Send, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const statusColor = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  booked: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  settled: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  manager_approved: 'bg-teal-100 text-teal-800 border-teal-200',
};

const ROLES_LIST = [
  { id: 'employee', label: '🧑‍💼 Employee (Raise)', desc: 'Raise requests & claims' },
  { id: 'manager', label: '👔 Manager (Approve)', desc: 'Approve team requests' },
  { id: 'travel_desk', label: '✈️ Travel Desk (Book)', desc: 'Issue tickets & PNR' },
  { id: 'finance', label: '💳 Finance (Settle)', desc: 'Audit & reimburse' },
  { id: 'admin', label: '🛡️ Admin (Govern)', desc: 'Policies & full control' },
];

const sampleSpendTrend = [
  { month: 'May', Flights: 45000, Hotels: 32000, Meals: 12000 },
  { month: 'Jun', Flights: 52000, Hotels: 41000, Meals: 15000 },
  { month: 'Jul', Flights: 38000, Hotels: 29000, Meals: 9000 },
  { month: 'Aug', Flights: 68000, Hotels: 55000, Meals: 18000 },
  { month: 'Sep', Flights: 74000, Hotels: 62000, Meals: 22000 },
];

const sampleCategoryData = [
  { name: 'Flights', value: 271000 },
  { name: 'Hotels', value: 219000 },
  { name: 'Meals & Per-Diem', value: 76000 },
  { name: 'Local Transit', value: 43000 },
];
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

function StatCard({ icon: Icon, label, value, sub, color = 'blue', href, tag }) {
  const content = (
    <Card className="hover:shadow-md transition-all duration-200 border-slate-200 bg-white relative overflow-hidden group h-full flex flex-col justify-between">
      <CardContent className="p-3 sm:p-4 lg:p-5 h-full flex flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
              {tag && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">{tag}</span>}
            </div>
            <p className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">{value ?? '—'}</p>
          </div>
          <div className={cn("p-2.5 rounded-xl border shadow-sm transition-transform group-hover:scale-105 shrink-0", {
            'bg-blue-50 border-blue-100 text-blue-600': color === 'blue',
            'bg-emerald-50 border-emerald-100 text-emerald-600': color === 'green',
            'bg-amber-50 border-amber-100 text-amber-600': color === 'amber',
            'bg-rose-50 border-rose-100 text-rose-600': color === 'red',
            'bg-purple-50 border-purple-100 text-purple-600': color === 'purple',
          })}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <div className="mt-2 text-xs font-medium">
          {sub ? (
            <p className="text-slate-500">{sub}</p>
          ) : (
            <span className="opacity-0 select-none block">&nbsp;</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
  return href ? <Link to={href} className="h-full block">{content}</Link> : content;
}

export default function DashboardPage() {
  const { user, switchRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTR, setRecentTR] = useState([]);
  const [recentEC, setRecentEC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, comment: '' });
  const [rejecting, setRejecting] = useState(false);

  const activeRole = user?.role || 'employee';

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, trRes, ecRes] = await Promise.all([
        reportsApi.dashboard(),
        travelRequestsApi.list({ limit: 10 }),
        expenseClaimsApi.list({ limit: 10 }),
      ]);
      setStats(statsRes.data);
      setRecentTR(trRes.data || []);
      setRecentEC(ecRes.data || []);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeRole]);

  // Quick Action Handler for Manager Approval
  const handleApproveTR = async (id) => {
    try {
      await travelRequestsApi.approve(id, { comment: 'Approved from dashboard' });
      toast.success('Travel request approved!');
      loadData();
    } catch { toast.error('Approval failed'); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal.id || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setRejecting(true);
    try {
      await travelRequestsApi.reject(rejectModal.id, { comment: rejectModal.comment });
      toast.success('Travel request rejected');
      setRejectModal({ open: false, id: null, comment: '' });
      loadData();
    } catch { toast.error('Rejection failed'); }
    finally { setRejecting(false); }
  };

  // Quick Action Handler for Finance Settlement
  const handleSettleEC = async (id) => {
    try {
      await expenseClaimsApi.settle(id, { payment_ref: 'NEFT-' + Math.floor(Math.random()*900000+100000) });
      toast.success('Claim settled & payment disbursed!');
      loadData();
    } catch { toast.error('Settlement failed'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">

      {/* ------------------- ROLE 1: EMPLOYEE DASHBOARD ------------------- */}
      {activeRole === 'employee' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-4 sm:p-5 lg:p-6 rounded-2xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold">🧑‍💼 Employee Travel & Expense Portal</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">Raise new travel requests, check itinerary PNRs, and submit post-trip expense claims.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/app/travel-requests/new" className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow flex items-center gap-1.5">
                <Plane className="w-4 h-4" /> Raise Travel Request
              </Link>
              <Link to="/app/expense-claims/new" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/20 flex items-center gap-1.5">
                <Receipt className="w-4 h-4" /> File Expense Claim
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
            <StatCard icon={Plane} tag="RAISE" label="My Active Trips" value={recentTR.filter(t => t.employee_id === user?.id).length || stats?.travel_requests?.reduce((a,b)=>a+b.count,0) || 0} color="blue" href="/app/travel-requests" />
            <StatCard icon={Receipt} tag="CLAIMS" label="My Expense Claims" value={recentEC.filter(e => e.employee_id === user?.id).length || stats?.expense_claims?.reduce((a,b)=>a+b.count,0) || 0} color="green" href="/app/expense-claims" />
            <StatCard icon={Wallet} tag="ADVANCES" label="My Cash Advances" value={stats?.outstanding_advances?.count || 0} sub={`₹${(stats?.outstanding_advances?.total || 0).toLocaleString('en-IN')}`} color="purple" href="/app/cash-advances" />
          </div>

          {/* Employee Trips List */}
          <Card className="border-slate-200 bg-white overflow-hidden shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between py-3.5 px-6 border-b bg-slate-50/50">
              <div>
                <CardTitle className="text-sm sm:text-base font-bold text-slate-900">My Travel Requests</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">Recent trip applications and booking statuses</p>
              </div>
              <Link to="/app/travel-requests" className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1">
                View All →
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {!recentTR.length ? (
                <div className="text-center py-10 text-slate-400 text-xs">No active travel requests found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Route / Purpose</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Travel Date</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Est. Budget</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Status</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentTR.slice(0, 5).map(tr => (
                        <tr key={tr.id} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Plane className="w-4 h-4" />
                              </div>
                              <div>
                                <span className="font-semibold text-slate-900 block group-hover:text-blue-600 transition-colors">
                                  {tr.origin ? `${tr.origin} ➔ ${tr.destination}` : tr.destination}
                                </span>
                                <span className="text-xs text-slate-500 truncate max-w-[200px] block">{tr.purpose || 'Business Travel'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium whitespace-nowrap">
                            {new Date(tr.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">
                            ₹{parseFloat(tr.budget_estimate || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 whitespace-nowrap">
                            <span className={cn("inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border capitalize", statusColor[tr.status])}>
                              <span className={cn("w-1.5 h-1.5 rounded-full", 
                                tr.status === 'approved' ? 'bg-blue-500' :
                                tr.status === 'booked' ? 'bg-indigo-500' :
                                tr.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                              )} />
                              {tr.status?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                            <Link to={`/app/travel-requests/${tr.id}`} className="text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                              View Details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ------------------- ROLE 2: MANAGER DASHBOARD ------------------- */}
      {activeRole === 'manager' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-900 to-slate-900 p-4 sm:p-5 lg:p-6 rounded-2xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold">👔 Manager Approval Center</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">Review team travel requests and expense claims awaiting your authorization.</p>
            </div>
            <Link to="/app/travel-requests?status=pending_approval" className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold shadow flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> View Pending Work Queue
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
            <StatCard icon={Clock} tag="ACTION REQ" label="Pending Travel Approvals" value={stats?.pending_travel_requests || recentTR.filter(t => t.status === 'pending_approval').length || 0} color="amber" href="/app/travel-requests?status=pending_approval" />
            <StatCard icon={Receipt} tag="ACTION REQ" label="Pending Expense Approvals" value={stats?.pending_expense_claims || recentEC.filter(e => e.status === 'pending_approval').length || 0} color="amber" href="/app/expense-claims?status=pending_approval" />
            <StatCard icon={TrendingUp} tag="BUDGET" label="Team Settled Spend" value={`₹${(stats?.team_settled_spend || 0).toLocaleString('en-IN')}`} color="green" href="/app/reports" />
          </div>

          {/* Pending Approval Queue with Direct Action Buttons */}
          <Card className="border-purple-200 bg-white overflow-hidden shadow-sm">
            <CardHeader className="py-3.5 px-6 border-b bg-purple-50/50">
              <CardTitle className="text-sm sm:text-base font-bold text-purple-950 flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-purple-600" /> Pending Manager Authorization Queue
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {recentTR.filter(t => t.status === 'pending_approval').length === 0 ? (
                <div className="text-center text-slate-400 text-xs py-10">No pending travel approvals in your queue ✓</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Employee</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Destination & Purpose</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Dates</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Est. Budget</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentTR.filter(t => t.status === 'pending_approval').map(tr => (
                        <tr key={tr.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-xs shrink-0">
                                {tr.employee_name?.charAt(0) || 'E'}
                              </div>
                              <span className="font-semibold text-slate-900">{tr.employee_name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                            <span className="font-medium text-slate-900 block">{tr.origin ? `${tr.origin} ➔ ${tr.destination}` : tr.destination}</span>
                            <span className="text-xs text-slate-500 truncate max-w-[220px] block italic">"{tr.purpose}"</span>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 whitespace-nowrap font-medium">
                            {new Date(tr.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">
                            ₹{parseFloat(tr.budget_estimate || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => handleApproveTR(tr.id)} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1 shadow-sm transition-colors">
                                <Check className="w-3.5 h-3.5" /> Approve
                              </button>
                              <button onClick={() => setRejectModal({ open: true, id: tr.id, comment: '' })} className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold flex items-center gap-1 shadow-sm transition-colors">
                                <XCircle className="w-3.5 h-3.5" /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ------------------- ROLE 3: TRAVEL DESK DASHBOARD ------------------- */}
      {activeRole === 'travel_desk' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-orange-900 to-slate-900 p-4 sm:p-5 lg:p-6 rounded-2xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold">✈️ Travel Desk Fulfillment Portal</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">Fulfill manager-approved travel requests by booking flights, hotels, and uploading PNR vouchers.</p>
            </div>
            <Link to="/app/vendors" className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold shadow flex items-center gap-1.5">
              <Building2 className="w-4 h-4" /> Manage Vendor Agencies
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
            <StatCard icon={Plane} tag="TO BOOK" label="Approved Requests Awaiting Booking" value={recentTR.filter(t => t.status === 'approved').length || 0} color="amber" href="/app/travel-requests?status=approved" />
            <StatCard icon={CheckCircle2} tag="FULFILLED" label="Completed Bookings" value={recentTR.filter(t => t.status === 'booked').length || 0} color="green" href="/app/travel-requests?status=booked" />
            <StatCard icon={Building2} tag="VENDORS" label="Active Vendor Partners" value="12" color="blue" href="/app/vendors" />
          </div>

          {/* Fulfillment Queue */}
          <Card className="border-orange-200 bg-white overflow-hidden shadow-sm">
            <CardHeader className="py-3.5 px-6 border-b bg-orange-50/50">
              <CardTitle className="text-sm sm:text-base font-bold text-orange-950 flex items-center gap-2">
                <Plane className="w-4 h-4 text-orange-600" /> Travel Fulfillment & Booking Queue
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {recentTR.filter(t => t.status === 'approved').length === 0 ? (
                <div className="text-center text-slate-400 text-xs py-10">No travel requests awaiting booking fulfillment ✓</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Employee</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Destination</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Travel Date</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentTR.filter(t => t.status === 'approved').map(tr => (
                        <tr key={tr.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                            <span className="font-semibold text-slate-900 block">{tr.employee_name}</span>
                            <span className="text-xs text-slate-500">{tr.employee_department || 'General'}</span>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-medium text-slate-900">
                            {tr.origin ? `${tr.origin} ➔ ${tr.destination}` : tr.destination}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">
                            {new Date(tr.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                            <Link to={`/app/travel-requests/${tr.id}`} className="px-4 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-sm transition-colors">
                              <Upload className="w-3.5 h-3.5" /> Upload PNR / Itinerary
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ------------------- ROLE 4: FINANCE DASHBOARD ------------------- */}
      {activeRole === 'finance' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-emerald-900 to-slate-900 p-4 sm:p-5 lg:p-6 rounded-2xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold">💳 Finance Settlement & Audit Portal</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">Audit manager-approved expense claims, verify policy compliance, and process reimbursements.</p>
            </div>
            <Link to="/app/reports" className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> Financial Spend Reports
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-stretch">
            <StatCard icon={Receipt} tag="PAYOUT" label="Claims Ready for Settlement" value={recentEC.filter(e => e.status === 'manager_approved').length || stats?.pending_settlement || 0} color="amber" href="/app/expense-claims?status=manager_approved" />
            <StatCard icon={Wallet} tag="ADVANCES" label="Outstanding Advances" value={stats?.outstanding_advances?.count || 0} sub={`₹${(stats?.outstanding_advances?.total || 0).toLocaleString('en-IN')}`} color="blue" href="/app/cash-advances" />
            <StatCard icon={AlertTriangle} tag="AUDIT" label="Policy Violations" value={stats?.total_violations || 0} color="red" href="/app/reports" />
            <StatCard icon={TrendingUp} tag="SETTLED" label="Total Settled (YTD)" value={`₹${(stats?.total_settled || 0).toLocaleString('en-IN')}`} color="green" href="/app/reports" />
          </div>

          {/* Settlement Queue */}
          <Card className="border-emerald-200 bg-white overflow-hidden shadow-sm">
            <CardHeader className="py-3.5 px-6 border-b bg-emerald-50/50">
              <CardTitle className="text-sm sm:text-base font-bold text-emerald-950 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-600" /> Claims Awaiting Reimbursement Payout
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {recentEC.filter(e => e.status === 'manager_approved').length === 0 ? (
                <div className="text-center text-slate-400 text-xs py-10">No manager-approved claims pending settlement ✓</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Claim Title</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Employee</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3">Reimbursable Amount</th>
                        <th className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentEC.filter(e => e.status === 'manager_approved').map(ec => (
                        <tr key={ec.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900">{ec.title}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">{ec.employee_name}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-emerald-700 whitespace-nowrap">
                            ₹{parseFloat(ec.net_reimbursable || ec.total_amount || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                            <button onClick={() => handleSettleEC(ec.id)} className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-sm transition-colors">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Settle & Reimburse
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ------------------- ROLE 5: ADMIN DASHBOARD ------------------- */}
      {activeRole === 'admin' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-950 via-slate-900 to-slate-950 p-4 sm:p-5 lg:p-6 rounded-2xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold">🛡️ Admin System Control & Audit Portal</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">Full governance oversight: policies, user permissions, multi-role approval overrides, and audit trails.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/app/policies" className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold shadow flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Configure Travel Policies
              </Link>
              <Link to="/app/reports" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold border border-white/20 flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> Master Audit Report
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-stretch">
            <StatCard icon={TrendingUp} tag="SPEND" label="Total System Spend" value={`₹${(stats?.total_settled || 0).toLocaleString('en-IN')}`} color="green" />
            <StatCard icon={Plane} tag="TRAVEL" label="Total Travel Requests" value={recentTR.length || 0} color="blue" href="/app/travel-requests" />
            <StatCard icon={AlertTriangle} tag="AUDIT" label="Policy Violations" value={stats?.total_violations || 0} color="red" href="/app/reports" />
            <StatCard icon={Users} tag="USERS" label="System Users & Roles" value="28" color="purple" href="/app/team" />
          </div>

          {/* Visual Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 bg-white">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-sm sm:text-base font-bold">Monthly Spend Trend</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sampleSpendTrend}>
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v)=>`₹${v/1000}k`} />
                      <Tooltip formatter={(v)=>[`₹${v.toLocaleString('en-IN')}`, 'Spend']} />
                      <Bar dataKey="Flights" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Hotels" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-sm sm:text-base font-bold">Category Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={sampleCategoryData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value">
                        {sampleCategoryData.map((_, i) => (
                          <Cell key={`c-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v)=>[`₹${v.toLocaleString('en-IN')}`, 'Total']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Reject Travel Request Modal */}
      <Dialog open={rejectModal.open} onOpenChange={open => setRejectModal(p => ({ ...p, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-slate-900">Reject Travel Request</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Please provide a reason for rejecting this travel request (at least 5 characters).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rejection Reason *</label>
              <textarea
                rows={3}
                value={rejectModal.comment}
                onChange={e => setRejectModal(p => ({ ...p, comment: e.target.value }))}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setRejectModal(p => ({ ...p, open: false }))}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRejectSubmit}
              disabled={rejecting || !rejectModal.comment.trim() || rejectModal.comment.trim().length < 5}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            >
              Confirm Rejection
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
